import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { StatusMessage } from "@repo/ui/components/ui/status-message";
import { Switch } from "@repo/ui/components/ui/switch";
import { Link } from "@tanstack/react-router";
import MDEditor from "@uiw/react-md-editor";
import { ArrowLeft, Eye, Loader2, Save, Trash2 } from "lucide-react";
import { MediaManager } from "@/lib/components/admin/MediaManager";
import { PlacePickerField } from "@/lib/components/PlacePickerField";
import { useExperienceEditor } from "@/lib/hooks/useExperienceEditor";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function ExperienceEditorPage(t0) {
  const $ = _c(8);
  const {
    experience
  } = t0;
  const isAdmin = useIsAdmin();
  const editor = useExperienceEditor(experience);
  if (!isAdmin) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-gray-500",
          children: "Du har inte beh\xF6righet att redigera."
        })
      });
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== editor) {
    t1 = /*#__PURE__*/_jsx(EditorToolbar, {
      editor: editor
    });
    $[1] = editor;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== editor) {
    t2 = /*#__PURE__*/_jsx("div", {
      className: "container mx-auto px-4 py-6",
      children: /*#__PURE__*/_jsxs("div", {
        className: "space-y-6",
        children: [/*#__PURE__*/_jsx(TitleSlugFields, {
          editor: editor
        }), /*#__PURE__*/_jsx(SubtitleField, {
          editor: editor
        }), /*#__PURE__*/_jsx(PlacePickerField, {
          editor: editor
        }), /*#__PURE__*/_jsx(PublishedToggle, {
          editor: editor
        }), /*#__PURE__*/_jsx(MediaSection, {
          editor: editor
        }), /*#__PURE__*/_jsx(DescriptionEditor, {
          editor: editor
        })]
      })
    });
    $[3] = editor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = /*#__PURE__*/_jsxs("div", {
      className: "min-h-screen bg-white",
      "data-color-mode": "light",
      children: [t1, t2]
    });
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function EditorToolbar(t0) {
  const $ = _c(24);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx(Button, {
      variant: "ghost",
      size: "sm",
      asChild: true,
      children: /*#__PURE__*/_jsxs(Link, {
        to: "/upptack",
        children: [/*#__PURE__*/_jsx(ArrowLeft, {
          className: "mr-1 h-4 w-4"
        }), "Tillbaka"]
      })
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = editor.isNew ? "Ny upplevelse" : `Redigerar: ${editor.form.title}`;
  let t3;
  if ($[1] !== t2) {
    t3 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-3",
      children: [t1, /*#__PURE__*/_jsx("span", {
        className: "text-sm text-gray-400",
        children: t2
      })]
    });
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== editor.form || $[4] !== editor.isNew) {
    t4 = !editor.isNew && /*#__PURE__*/_jsx(Button, {
      variant: "ghost",
      size: "sm",
      asChild: true,
      children: /*#__PURE__*/_jsxs(Link, {
        to: "/upptack/$slug",
        params: {
          slug: editor.form.titleSlug
        },
        children: [/*#__PURE__*/_jsx(Eye, {
          className: "mr-1 h-4 w-4"
        }), "F\xF6rhandsgranska"]
      })
    });
    $[3] = editor.form;
    $[4] = editor.isNew;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== editor.isSaving) {
    t5 = editor.isSaving ? /*#__PURE__*/_jsx(Loader2, {
      className: "h-4 w-4 animate-spin"
    }) : /*#__PURE__*/_jsx(Save, {
      className: "h-4 w-4"
    });
    $[6] = editor.isSaving;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = editor.isNew ? "Skapa" : "Spara";
  let t7;
  if ($[8] !== editor.isSaving || $[9] !== editor.save || $[10] !== t5 || $[11] !== t6) {
    t7 = /*#__PURE__*/_jsxs(Button, {
      onClick: editor.save,
      disabled: editor.isSaving,
      size: "sm",
      className: "gap-1.5 bg-brand hover:bg-brand/90",
      children: [t5, t6]
    });
    $[8] = editor.isSaving;
    $[9] = editor.save;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== editor.isDeleting || $[14] !== editor.isNew || $[15] !== editor.remove) {
    t8 = !editor.isNew && /*#__PURE__*/_jsx(Button, {
      variant: "destructive",
      size: "sm",
      disabled: editor.isDeleting,
      onClick: editor.remove,
      children: /*#__PURE__*/_jsx(Trash2, {
        className: "h-4 w-4"
      })
    });
    $[13] = editor.isDeleting;
    $[14] = editor.isNew;
    $[15] = editor.remove;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t7 || $[19] !== t8) {
    t9 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-2",
      children: [t4, t7, t8]
    });
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t3 || $[22] !== t9) {
    t10 = /*#__PURE__*/_jsx("div", {
      className: "sticky top-14 z-40 border-b bg-white/95 backdrop-blur-sm",
      children: /*#__PURE__*/_jsxs("div", {
        className: "container mx-auto flex items-center justify-between px-4 py-2",
        children: [t3, t9]
      })
    });
    $[21] = t3;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function TitleSlugFields(t0) {
  const $ = _c(15);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx(Label, {
      htmlFor: "title",
      children: "Titel *"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== editor) {
    t2 = e => editor.updateField("title", e.target.value);
    $[1] = editor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== editor.form.title || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t1, /*#__PURE__*/_jsx(Input, {
        id: "title",
        value: editor.form.title,
        onChange: t2,
        placeholder: "Namn p\xE5 upplevelsen",
        className: "text-lg"
      })]
    });
    $[3] = editor.form.title;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/_jsx(Label, {
      htmlFor: "slug",
      children: "URL-slug *"
    });
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== editor) {
    t5 = e_0 => {
      editor.disableAutoSlug();
      editor.updateField("titleSlug", e_0.target.value);
    };
    $[7] = editor;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== editor.form.titleSlug || $[10] !== t5) {
    t6 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t4, /*#__PURE__*/_jsx(Input, {
        id: "slug",
        value: editor.form.titleSlug,
        onChange: t5,
        placeholder: "url-vanligt-namn"
      })]
    });
    $[9] = editor.form.titleSlug;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t6) {
    t7 = /*#__PURE__*/_jsxs("div", {
      className: "grid gap-4 md:grid-cols-2",
      children: [t3, t6]
    });
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function SubtitleField(t0) {
  const $ = _c(6);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx(Label, {
      htmlFor: "subtitle",
      children: "Underrubrik"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== editor) {
    t2 = e => editor.updateField("subtitle", e.target.value);
    $[1] = editor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== editor.form.subtitle || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t1, /*#__PURE__*/_jsx(Input, {
        id: "subtitle",
        value: editor.form.subtitle,
        onChange: t2,
        placeholder: "Kort sammanfattning som visas under titeln"
      })]
    });
    $[3] = editor.form.subtitle;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function PublishedToggle(t0) {
  const $ = _c(6);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Label, {
        className: "text-base",
        children: "Publicerad"
      }), /*#__PURE__*/_jsx("p", {
        className: "text-sm text-gray-500",
        children: "Synlig f\xF6r alla bes\xF6kare p\xE5 upplevelser-sidan"
      })]
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== editor) {
    t2 = checked => editor.updateField("isPublished", checked);
    $[1] = editor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== editor.form.isPublished || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center justify-between rounded-lg border p-4",
      children: [t1, /*#__PURE__*/_jsx(Switch, {
        checked: editor.form.isPublished,
        onCheckedChange: t2
      })]
    });
    $[3] = editor.form.isPublished;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// Extracted to its own file to avoid linter stripping the import
function MediaSection(t0) {
  const $ = _c(5);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx(Label, {
      children: "Bilder"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== editor.mediaItems || $[2] !== editor.mediaStatus || $[3] !== editor.setMediaItems) {
    t2 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t1, editor.mediaStatus === "ready" ? /*#__PURE__*/_jsx(MediaManager, {
        items: editor.mediaItems,
        onChange: editor.setMediaItems
      }) : editor.mediaStatus === "loading" ? /*#__PURE__*/_jsx("p", {
        className: "text-sm text-muted-foreground",
        children: "Laddar bilder\u2026"
      }) : /*#__PURE__*/_jsx("div", {
        role: "alert",
        children: /*#__PURE__*/_jsx(StatusMessage, {
          variant: "error",
          message: "Kunde inte l\xE4sa in bilderna. Texten kan sparas, men bilderna l\xE4mnas of\xF6r\xE4ndrade. Ladda om sidan f\xF6r att redigera bilder."
        })
      })]
    });
    $[1] = editor.mediaItems;
    $[2] = editor.mediaStatus;
    $[3] = editor.setMediaItems;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function DescriptionEditor(t0) {
  const $ = _c(7);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx(Label, {
      children: "Beskrivning (Markdown)"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== editor) {
    t2 = val => editor.updateField("description", val || "");
    $[1] = editor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      placeholder: "Skriv upplevelsens inneh\xE5ll h\xE4r... Markdown st\xF6ds."
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== editor.form.description || $[5] !== t2) {
    t4 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2",
      children: [t1, /*#__PURE__*/_jsx("div", {
        className: "min-h-[600px] overflow-hidden rounded-lg border",
        children: /*#__PURE__*/_jsx(MDEditor, {
          value: editor.form.description,
          onChange: t2,
          preview: "live",
          height: 700,
          visibleDragbar: false,
          textareaProps: t3
        })
      })]
    });
    $[4] = editor.form.description;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}